// UP11 (upstream #7254 reference): the renderer's view of the Desk's browser
// profiles, and which profile each preview tab was opened under.
//
// A tab's profile is fixed at open: Electron honours a <webview>'s partition
// only before the guest attaches. The preview session snapshot the server
// keeps carries no profile, so the choice lives here — keyed by thread and
// server tab id, kept in this browser's storage so a reload mounts the tab
// where it was. A tab with no record runs in the built-in Default partition,
// the scope the browser used before profiles, never in whatever profile is
// configured as the default now: the configured default is resolved once, at
// open, and remembered for the tab.
import { scopedThreadKey } from "@cleo/client-runtime/environment";
import {
  DEFAULT_BROWSER_PROFILE_ID,
  type DesktopBrowserProfile,
  type DesktopBrowserProfilesState,
  type ScopedThreadRef,
} from "@cleo/contracts";
import { useEffect } from "react";
import { create } from "zustand";

import { previewBridge } from "./previewBridge";

const STORAGE_KEY = "cleo:preview-tab-profiles:v1";
/** Tabs closed on the server while this client was away would otherwise pile up. */
const MAX_REMEMBERED_TABS = 200;
const KEY_DELIMITER = "\u0000";

interface RememberedTabProfile {
  readonly profileId: string;
  readonly rememberedAt: number;
}

interface PreviewBrowserProfilesStore {
  readonly profilesState: DesktopBrowserProfilesState | null;
  readonly byTabKey: Readonly<Record<string, RememberedTabProfile>>;
  readonly setProfilesState: (state: DesktopBrowserProfilesState | null) => void;
  readonly remember: (tabKey: string, profileId: string) => void;
  readonly forget: (tabKey: string) => void;
  readonly reset: () => void;
}

function readStoredTabProfiles(): Record<string, RememberedTabProfile> {
  try {
    if (typeof window === "undefined") return {};
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
    const result: Record<string, RememberedTabProfile> = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value !== "object" || value === null) continue;
      const candidate = value as { profileId?: unknown; rememberedAt?: unknown };
      if (typeof candidate.profileId !== "string" || candidate.profileId.length === 0) continue;
      result[key] = {
        profileId: candidate.profileId,
        rememberedAt: typeof candidate.rememberedAt === "number" ? candidate.rememberedAt : 0,
      };
    }
    return result;
  } catch {
    return {};
  }
}

function writeStoredTabProfiles(byTabKey: Readonly<Record<string, RememberedTabProfile>>): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(byTabKey));
  } catch {
    // Storage is a convenience: without it a reload mounts the tab under Default.
  }
}

function pruneTabProfiles(
  byTabKey: Readonly<Record<string, RememberedTabProfile>>,
): Readonly<Record<string, RememberedTabProfile>> {
  const entries = Object.entries(byTabKey);
  if (entries.length <= MAX_REMEMBERED_TABS) return byTabKey;
  entries.sort((left, right) => right[1].rememberedAt - left[1].rememberedAt);
  return Object.fromEntries(entries.slice(0, MAX_REMEMBERED_TABS));
}

const useStore = create<PreviewBrowserProfilesStore>()((set) => ({
  profilesState: null,
  byTabKey: readStoredTabProfiles(),
  setProfilesState: (profilesState) => set({ profilesState }),
  remember: (tabKey, profileId) =>
    set((state) => {
      if (state.byTabKey[tabKey]?.profileId === profileId) return state;
      const byTabKey = pruneTabProfiles({
        ...state.byTabKey,
        [tabKey]: { profileId, rememberedAt: Date.now() },
      });
      writeStoredTabProfiles(byTabKey);
      return { byTabKey };
    }),
  forget: (tabKey) =>
    set((state) => {
      if (!(tabKey in state.byTabKey)) return state;
      const { [tabKey]: _dropped, ...byTabKey } = state.byTabKey;
      writeStoredTabProfiles(byTabKey);
      return { byTabKey };
    }),
  reset: () => {
    writeStoredTabProfiles({});
    set({ profilesState: null, byTabKey: {} });
  },
}));

export function previewTabProfileKey(threadRef: ScopedThreadRef, tabId: string): string {
  return `${scopedThreadKey(threadRef)}${KEY_DELIMITER}${tabId}`;
}

/** Records the profile a tab was opened under; the tab keeps it for its lifetime. */
export function rememberPreviewTabProfile(
  threadRef: ScopedThreadRef,
  tabId: string,
  profileId: string,
): void {
  useStore.getState().remember(previewTabProfileKey(threadRef, tabId), profileId);
}

/** The profile a tab was opened under, or undefined for a tab with no record. */
export function readPreviewTabProfile(
  threadRef: ScopedThreadRef,
  tabId: string,
): string | undefined {
  return useStore.getState().byTabKey[previewTabProfileKey(threadRef, tabId)]?.profileId;
}

export function forgetPreviewTabProfile(threadRef: ScopedThreadRef, tabId: string): void {
  useStore.getState().forget(previewTabProfileKey(threadRef, tabId));
}

export function usePreviewTabProfile(
  threadRef: ScopedThreadRef,
  tabId: string | null,
): string | undefined {
  const key = tabId === null ? null : previewTabProfileKey(threadRef, tabId);
  return useStore((state) => (key === null ? undefined : state.byTabKey[key]?.profileId));
}

/** The partition a tab runs in when it carries no record: the built-in Default. */
export function previewTabProfileId(remembered: string | undefined): string {
  return remembered ?? DEFAULT_BROWSER_PROFILE_ID;
}

export function previewProfileName(
  profiles: ReadonlyArray<Pick<DesktopBrowserProfile, "id" | "name">>,
  profileId: string,
): string {
  return profiles.find((profile) => profile.id === profileId)?.name ?? "Removed profile";
}

let inFlight: Promise<DesktopBrowserProfilesState | null> | null = null;

/**
 * The Desk's profile list, read once through the profile door and kept until
 * the settings section publishes a newer one. Null without the door (a
 * browser, or an older Desk) or when the read fails.
 */
export function loadBrowserProfilesState(): Promise<DesktopBrowserProfilesState | null> {
  const door = previewBridge?.browserProfiles;
  if (!door) return Promise.resolve(null);
  const cached = useStore.getState().profilesState;
  if (cached !== null) return Promise.resolve(cached);
  inFlight ??= door
    .list()
    .then((state) => {
      useStore.getState().setProfilesState(state);
      return state;
    })
    .catch(() => null)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** The settings section publishes every state the Desk returns from a mutation. */
export function publishBrowserProfilesState(state: DesktopBrowserProfilesState): void {
  useStore.getState().setProfilesState(state);
}

export function useBrowserProfilesState(): DesktopBrowserProfilesState | null {
  const state = useStore((store) => store.profilesState);
  useEffect(() => {
    if (state === null) void loadBrowserProfilesState();
  }, [state]);
  return state;
}

/** The configured default a new tab opens under; undefined without the door. */
export async function resolveDefaultBrowserProfileId(): Promise<string | undefined> {
  return (await loadBrowserProfilesState())?.defaultProfileId;
}

export function resetPreviewBrowserProfilesForTests(): void {
  inFlight = null;
  useStore.getState().reset();
}
